import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Anchor } from "lucide-react";
import { PERCORSO_OPTIONS, DIAGNOSI_OPTIONS, CICLO_OPTIONS } from "@/lib/site";
import { Stamp, WaveBand } from "@/components/decor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  nome: "",
  cognome: "",
  data_nascita: "",
  telefono: "",
  email: "",
  diagnosi_adhd: "",
  tempo_diagnosi: "",
  scelta_percorso: "gruppo_presenza",
};

const inputCls =
  "w-full rounded-xl border border-[#E2D9CD] bg-[#FAF8F5] px-4 py-3 text-sm text-[#0C1B33] outline-none transition-[border-color,box-shadow,background-color] duration-300 placeholder:text-[#94A3B8] focus:border-[#0D9488] focus:bg-white focus:shadow-[0_0_0_3px_rgba(13,148,136,0.15)]";

const Field = ({ label, htmlFor, required = true, children, testId }) => (
  <div data-testid={testId}>
    <label htmlFor={htmlFor} className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#0C1B33]/70">
      {label} {required && <span className="text-[#C93B2B]">*</span>}
    </label>
    {children}
  </div>
);

const Pill = ({ active, onClick, children, testId, warn = false }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 ${
      warn
        ? active
          ? "border-amber-500 bg-amber-500 text-white shadow-[0_6px_18px_rgba(245,158,11,0.3)]"
          : "border-amber-400/70 bg-amber-50 text-amber-800 hover:border-amber-500"
        : active
          ? "border-[#0D9488] bg-[#0D9488] text-white shadow-[0_6px_18px_rgba(13,148,136,0.3)]"
          : "border-[#E2D9CD] bg-[#FAF8F5] text-[#0C1B33]/75 hover:border-[#0D9488]/60"
    }`}
  >
    {children}
  </button>
);

const IscrizioneForm = ({ percorso, setPercorso, ciclo, setCiclo, posti, onIscritto }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const modKey = percorso === "gruppo_online" ? "online" : "presenza";
  const cicloKey = `${modKey}_${ciclo === "ciclo_2" ? "2" : "1"}`;
  const infoSel = percorso === "individuale" ? null : posti?.cicli?.[cicloKey];
  const esauritoSel = Boolean(infoSel && infoSel.rimasti <= 0);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.diagnosi_adhd) {
      toast.error("Indica se hai già una diagnosi di ADHD.");
      return;
    }
    const esaurito = esauritoSel;
    setSending(true);
    try {
      await axios.post(`${API}/iscrizioni`, {
        ...form,
        scelta_percorso: percorso,
        ciclo: percorso === "individuale" ? null : ciclo,
      });
      if (esaurito) {
        toast.success("Iscrizione in lista d'attesa registrata!", {
          description: "I posti sono esauriti: ti ricontatteremo appena se ne libera uno.",
        });
      } else {
        toast.success("Richiesta inviata!", {
          description: "Grazie per esserti iscritto: la partecipazione ti verrà confermata personalmente.",
        });
      }
      setForm(initialForm);
      onIscritto?.();
    } catch (err) {
      toast.error("Qualcosa è andato storto", {
        description: "Riprova tra poco oppure scrivici a pipitone@hin.ch.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="iscrizione" className="relative overflow-hidden bg-[#FAF8F5] pb-0 pt-20 sm:pt-28" data-testid="iscrizione-section">
      <div
        aria-hidden
        className="absolute -left-32 top-24 h-[420px] w-[420px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(13,148,136,0.14), transparent 65%)" }}
      />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-10 text-center"
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#0D9488]">05 — Iscrizione</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[#0C1B33] sm:text-5xl">
            Salpa con noi
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[#475569]">
            Compila il modulo: la tua richiesta arriva direttamente allo Studio e la partecipazione
            ti verrà confermata personalmente. I posti sono limitati, per garantire un'atmosfera
            d'ascolto protetta.
          </p>
        </motion.div>

        <motion.form
          data-testid="iscrizione-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75 }}
          className="relative rounded-3xl border border-[#E2D9CD] bg-[#FFFDF9] p-6 shadow-[0_24px_60px_rgba(12,27,51,0.1)] sm:p-10"
        >
          <Stamp small testId="form-stamp" className="absolute -right-3 -top-8 h-24 w-24 rotate-12 bg-[#FAF8F5]/80" />

          <div className="mb-8" data-testid="form-percorso-field">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0C1B33]/70">
              Percorso desiderato <span className="text-[#C93B2B]">*</span>
            </p>
            <div className="flex flex-wrap gap-3">
              {PERCORSO_OPTIONS.map((opt) => {
                const info = posti?.[opt.value];
                const esaurito = Boolean(info && info.rimasti <= 0);
                return (
                  <Pill
                    key={opt.value}
                    testId={`form-percorso-${opt.value}`}
                    active={percorso === opt.value}
                    warn={esaurito}
                    onClick={() => setPercorso(opt.value)}
                  >
                    <span className="block">{opt.label}</span>
                    <span className={`block text-[10px] ${percorso === opt.value && !esaurito ? "text-white/80" : esaurito ? (percorso === opt.value ? "text-white/90" : "text-amber-700") : "text-[#718096]"}`}>
                      {esaurito ? "Posti esauriti · lista d'attesa" : opt.detail}
                    </span>
                  </Pill>
                );
              })}
            </div>
            {(percorso === "gruppo_presenza" || percorso === "gruppo_online") && (
              <div className="mt-5" data-testid="form-ciclo-field">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0C1B33]/70">
                  Quale ciclo 2027 preferisci? <span className="text-[#C93B2B]">*</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {CICLO_OPTIONS.map((opt) => {
                    const infoC = posti?.cicli?.[`${modKey}_${opt.value === "ciclo_2" ? "2" : "1"}`];
                    const pieno = Boolean(infoC && infoC.rimasti <= 0);
                    return (
                      <Pill
                        key={opt.value}
                        testId={`form-ciclo-${opt.value}`}
                        active={ciclo === opt.value}
                        warn={pieno}
                        onClick={() => setCiclo(opt.value)}
                      >
                        <span className="block">{opt.label}</span>
                        <span className={`block text-[10px] ${ciclo === opt.value ? "text-white/85" : pieno ? "text-amber-700" : "text-[#718096]"}`}>
                          {pieno
                            ? `${opt.detail} · posti esauriti`
                            : infoC
                              ? `${opt.detail} · ${infoC.rimasti} posti rimasti`
                              : opt.detail}
                        </span>
                      </Pill>
                    );
                  })}
                </div>
              </div>
            )}
            {esauritoSel && (
              <div data-testid="form-lista-attesa-notice" className="mt-4 rounded-xl border border-amber-400/50 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                I posti per questo gruppo sono esauriti: inviando il modulo verrai inserito/a in
                <strong> lista d'attesa</strong>, con priorità per i prossimi cicli — le date 2027
                sono già pubblicate nel calendario.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Nome" htmlFor="nome" testId="field-nome">
              <input id="nome" data-testid="input-nome" type="text" required maxLength={100}
                placeholder="Il tuo nome" className={inputCls} value={form.nome} onChange={set("nome")} />
            </Field>
            <Field label="Cognome" htmlFor="cognome" testId="field-cognome">
              <input id="cognome" data-testid="input-cognome" type="text" required maxLength={100}
                placeholder="Il tuo cognome" className={inputCls} value={form.cognome} onChange={set("cognome")} />
            </Field>
            <Field label="Data di nascita" htmlFor="data_nascita" testId="field-data-nascita">
              <input id="data_nascita" data-testid="input-data-nascita" type="date" required
                className={inputCls} value={form.data_nascita} onChange={set("data_nascita")} />
            </Field>
            <Field label="Numero di telefono" htmlFor="telefono" testId="field-telefono">
              <input id="telefono" data-testid="input-telefono" type="tel" required maxLength={30}
                placeholder="+41 ..." className={inputCls} value={form.telefono} onChange={set("telefono")} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Email" htmlFor="email" testId="field-email">
                <input id="email" data-testid="input-email" type="email" required
                  placeholder="nome@esempio.ch" className={inputCls} value={form.email} onChange={set("email")} />
              </Field>
            </div>

            <div className="sm:col-span-2" data-testid="field-diagnosi">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0C1B33]/70">
                Hai già una diagnosi di ADHD? <span className="text-[#C93B2B]">*</span>
              </p>
              <div className="flex flex-wrap gap-3">
                {DIAGNOSI_OPTIONS.map((opt) => (
                  <Pill
                    key={opt.value}
                    testId={`form-diagnosi-${opt.value}`}
                    active={form.diagnosi_adhd === opt.value}
                    onClick={() => setForm((f) => ({ ...f, diagnosi_adhd: opt.value }))}
                  >
                    {opt.label}
                  </Pill>
                ))}
              </div>
            </div>

            <div className="sm:col-span-2">
              <Field label="Da quanto tempo hai la diagnosi?" htmlFor="tempo_diagnosi" required={false} testId="field-tempo-diagnosi">
                <input id="tempo_diagnosi" data-testid="input-tempo-diagnosi" type="text" maxLength={200}
                  placeholder="es. da 6 mesi, da 2 anni, non ancora diagnosticato/a"
                  className={inputCls} value={form.tempo_diagnosi} onChange={set("tempo_diagnosi")} />
              </Field>
            </div>
          </div>

          <button
            type="submit"
            data-testid="form-submit-button"
            disabled={sending}
            className="group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0C1B33] px-8 py-4 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(12,27,51,0.3)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#0D9488] hover:shadow-[0_16px_40px_rgba(13,148,136,0.4)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            )}
            {sending
              ? "Invio in corso…"
              : esauritoSel
                ? "Iscriviti alla lista d'attesa"
                : "Invia richiesta di iscrizione"}
          </button>

          <p className="mt-5 flex items-center gap-2 text-xs text-[#718096]" data-testid="form-privacy-note">
            <Anchor className="h-3.5 w-3.5 text-[#0D9488]" />
            I tuoi dati vengono inviati solo allo studio e non saranno condivisi con terzi.
          </p>
        </motion.form>
      </div>

      <WaveBand className="mt-20 h-20 sm:h-28" tone="navy" />
    </section>
  );
};

export default IscrizioneForm;
